<template>
  <div>
    <!-- 表示範囲の河川予警報 -->
    <div v-for="(value, key) in getRwGeoJson" v-bind:key="key">
      <l-geo-json
        :geojson="value"
        :options="options"
        :pane="setPane(value)"
        @click="clickRiverWarnList"
      />
    </div>
    <!-- 選択中河川予警報 -->
    <l-geo-json
      v-if="selectedRwGeoJson"
      :geojson="selectedRwGeoJson"
      :options="selectedOptions"
      pane="selectRwPane"
    />
  </div>
</template>
<!-- ----------------------------------------------- -->
<script>
import { LGeoJson } from "vue2-leaflet";
import mixin from "../../../lib/mixin.js";
import WebStorage from "../../../lib/webStorage.js";
import Util from "../../../lib/util.js";
import MapUtil from "../../../lib/mapUtil.js";
import Color from "../../../scss/color.scss";

/**
 * 川の防災情報 地図画面 河川予警報レイヤー
 * RiverWarnLayer.vue
 * @author LIS
 */

export default {
  name: "river-warn-layer",
  mixins: [mixin],
  components: {
    LGeoJson,
  },
  /** プロパティ部 */
  props: {
    mapNum: {
      type: Number,
      default: 0,
    },
  },
  /** 初期化部 */
  mounted() {
    // GeoJsonを取得
    this.getRiverWarnGeoJson();
    this.getSelectedRwGeoJson();
  },
  /** 算出部 */
  computed: {
    /** 地図設定情報 */
    mapConfig: {
      get() {
        return this.$store.state.map.mapConfig;
      },
    },
    /** 設定情報 */
    config: {
      get() {
        return this.$store.state.app.config;
      },
    },
    /** 河川予警報の座標を取得 */
    getRwGeoJson: {
      get() {
        if (
          this.$store.state.RiverWarn.rwGeoJson &&
          this.$store.state.app.appTime
        ) {
          // テレメータGeoJsonの中から該当日時のデータを抽出
          const dateIndex = this.$store.state.RiverWarn.rwGeoJson.findIndex(
            (info) => info.date == this.baseTime
          );
          if (dateIndex >= 0) {
            return this.$store.state.RiverWarn.rwGeoJson[dateIndex]
              .rwGeoJsonInfo;
          }
        }
        return [];
      },
    },
    /** 表示市町村取得時刻 */
    getTownsAcqTime: {
      get() {
        return this.$store.state.map.townsAcqTime;
      },
    },
    /** 表示コンテンツを取得 */
    getContent: {
      get() {
        return this.$store.state.app.content;
      },
    },
    /** 地図の表示時間間隔 */
    radarInterval: {
      get() {
        return this.$store.state.radar.radarInterval;
      },
    },
    /** 基準時間を計算 */
    baseTime: {
      get() {
        let returnTime;
        if (this.$store.state.radar.radarNumber !== 4) {
          // 1枚表示の場合は、appストアの観測日時を基準にする、
          returnTime = this.$store.state.app.appTime;
        } else {
          // 4枚表示の場合は、レーダ観測日時を基準にする
          let radarTime;
          if (this.$store.state.radar.radarType == "xrain") {
            radarTime = this.$store.state.radar.radarXrainDate;
          } else {
            radarTime = this.$store.state.radar.radarCbandDate;
          }
          returnTime = MapUtil.calcBaseTime(
            radarTime,
            this.mapNum,
            this.$store.state.radar.radarInterval
          );
        }
        // 5分or10分間隔に切り捨て、文字列変換
        let roundTime = Util.roundMinutes(
          returnTime,
          this.$store.state.app.config.appConfig.keepDaysJsonFiles,
          this.$store.state.app.appTimeRoundRw
        );
        return Util.formatDate(roundTime, "YYYY-MM-DD HH:mm");
      },
    },
    /** 予警報種別ごとの表示設定 */
    options: {
      get() {
        if (
          this.$store.state.map.editWebStorageDate &&
          this.$store.state.map.viewGroup >= 0 &&
          this.$store.state.map.zoom &&
          (this.getContent || this.getContent == "")
        ) {
          return {
            style: (feature) => {
              const type = feature.properties.type;
              const lvl = feature.properties.lvl;
              let color = null;
              let rwLineConfig = {};

              // 予警報種別ごとの予警報区間の描画設定
              // 洪水予報
              if (type === 10) {
                // ズームレベルごとの線の太さと透過度の設定
                rwLineConfig = this.setRwLineConfig(
                  this.mapConfig.riverWarnLine.fldfr
                );

                // 危険度ごとの色の設定
                switch (lvl) {
                  // 氾濫注意水位
                  case 30:
                    color = Color.rwStgWarn;
                    break;
                  // 避難判断水位
                  case 60:
                    color = Color.rwStgRefuge;
                    break;
                  // 氾濫危険水位
                  case 80:
                    color = Color.rwStgDanger;
                    break;
                  // 氾濫発生
                  case 90:
                    color = Color.rwStgOver;
                }

                // 水位周知河川情報
              } else if (type === 11) {
                // ズームレベルごとの線の太さと透過度の設定
                rwLineConfig = this.setRwLineConfig(
                  this.mapConfig.riverWarnLine.evstg
                );

                // 危険度ごとの色の設定
                switch (lvl) {
                  // 氾濫注意水位
                  case 30:
                    color = Color.rwStgWarn;
                    break;
                  // 避難判断水位
                  case 60:
                    color = Color.rwStgRefuge;
                    break;
                  // 氾濫危険水位
                  case 80:
                    color = Color.rwStgDanger;
                    break;
                  // 氾濫発生
                  case 90:
                    color = Color.rwStgOver;
                }

                // 水防警報
              } else if (type === 20) {
                // ズームレベルごとの線の太さと透過度の設定
                rwLineConfig = this.setRwLineConfig(
                  this.mapConfig.riverWarnLine.fldctl
                );

                // 危険度ごとの色の設定
                switch (lvl) {
                  // 待機
                  case 10:
                    color = Color.rwFldctlStandby;
                    break;
                  // 準備
                  case 20:
                    color = Color.rwFldctlPreparation;
                    break;
                  // 出動
                  case 40:
                    color = Color.rwFldctlDispatch;
                }
              }

              let rWOption = {
                color: color,
                weight: rwLineConfig.weight,
                opacity: rwLineConfig.opacity,
              };

              return rWOption;
            },
            filter: (feature) => {
              const type = feature.properties.type;
              let viewGroup = this.$store.state.map.viewGroup;
              let customInfo = WebStorage.getCustomInfo();

              if (this.getContent === "VcCustom") {
                // カスタマイズ設定編集モードの場合、カスタマイズ設定に従って予警報を表示する。
                if (type === 10) {
                  // 洪水予報
                  if (
                    this.$store.state.map.zoom >= this.mapConfig.zoomShowFldfrs
                  ) {
                    return customInfo.fldfr;
                  }
                } else if (type === 11) {
                  // 水位周知河川情報
                  if (
                    this.$store.state.map.zoom >= this.mapConfig.zoomShowEvstgs
                  ) {
                    return customInfo.fldfr;
                  }
                } else if (type === 20) {
                  // 水防警報
                  if (
                    this.$store.state.map.zoom >= this.mapConfig.zoomShowFldctls
                  ) {
                    return customInfo.fldctl;
                  }
                }
              } else if (
                this.getContent === "RiverwarnList" ||
                this.getContent === "Riverwarn"
              ) {
                // 予警報画面表示中は、表示設定に関わらず表示する。
                if (type === 10) {
                  // 洪水予報
                  if (
                    this.$store.state.map.zoom >= this.mapConfig.zoomShowFldfrs
                  ) {
                    return true;
                  }
                } else if (type === 11) {
                  // 水位周知河川情報
                  if (
                    this.$store.state.map.zoom >= this.mapConfig.zoomShowEvstgs
                  ) {
                    return true;
                  }
                } else if (type === 20) {
                  // 水防警報
                  // 水防団用表示が選択されている場合
                  if (this.$store.getters["app/isFldCtl"]) {
                    if (
                      this.$store.state.map.zoom >=
                      this.mapConfig.zoomShowFldctls
                    ) {
                      return true;
                    }
                  }
                }
              } else {
                // その他の場合、表示設定に従って予警報を表示する。
                if (type === 10) {
                  // 洪水予報
                  if (
                    this.$store.state.map.zoom >= this.mapConfig.zoomShowFldfrs
                  ) {
                    if (viewGroup === 5) {
                      return customInfo.fldfr;
                    }
                    return true;
                  }
                } else if (type === 11) {
                  // 水位周知河川情報
                  if (
                    this.$store.state.map.zoom >= this.mapConfig.zoomShowEvstgs
                  ) {
                    if (viewGroup === 5) {
                      return customInfo.fldfr;
                    }
                    return true;
                  }
                } else if (type === 20) {
                  // 水防警報
                  // 水防団用表示が選択されている場合
                  if (this.$store.getters["app/isFldCtl"]) {
                    if (
                      this.$store.state.map.zoom >=
                      this.mapConfig.zoomShowFldctls
                    ) {
                      if (viewGroup === 5) {
                        return customInfo.fldctl;
                      }
                      return true;
                    }
                  }
                }
              }
              return false;
            },
          };
        }
        return null;
      },
    },
    /** 選択中河川予警報GeoJsonの表示設定 */
    selectedOptions: {
      get() {
        if (
          this.$store.state.map.zoom &&
          (this.getContent || this.getContent == "")
        ) {
          return {
            style: () => {
              const type = this.selectedWarnReq.type;
              let rwLineConfig = {};

              // 線の太さと透過度の設定は表示範囲の予警報表示と同等
              if (type === 10) {
                rwLineConfig = this.setRwLineConfig(
                  this.mapConfig.riverWarnLine.fldfr
                );
              } else if (type === 11) {
                rwLineConfig = this.setRwLineConfig(
                  this.mapConfig.riverWarnLine.evstg
                );
              } else if (type === 20) {
                rwLineConfig = this.setRwLineConfig(
                  this.mapConfig.riverWarnLine.fldctl
                );
              }
              let rWOption = {
                color: Color.mapSelectRw,
                weight: rwLineConfig.weight,
                opacity: rwLineConfig.opacity,
              };

              return rWOption;
            },
            filter: () => {
              // 予警報詳細画面表示中以外は表示しない
              if (this.getContent != "Riverwarn" || !this.selectedWarnReq) {
                return false;
              }
              const type = this.selectedWarnReq.type;
              // ズームレベル設定は表示範囲の予警報表示と同等
              if (type === 10) {
                if (
                  this.$store.state.map.zoom >= this.mapConfig.zoomShowFldfrs
                ) {
                  return true;
                }
              } else if (type === 11) {
                if (
                  this.$store.state.map.zoom >= this.mapConfig.zoomShowEvstgs
                ) {
                  return true;
                }
              } else if (type === 20) {
                if (
                  this.$store.state.map.zoom >= this.mapConfig.zoomShowFldctls
                ) {
                  return true;
                }
              }
              return false;
            },
          };
        }
        return null;
      },
    },
    /** レーダ履歴動画再生状態 */
    radarHistoryPlayStatus: {
      get() {
        return this.$store.state.radar.historyPlayStatus;
      },
    },
    /** 選択中河川予警報リクエストキー */
    selectedWarnReq: {
      get() {
        return this.$store.state.RiverWarn.selectedWarnReq;
      },
    },
    /** 選択中河川予警報GeoJson */
    selectedRwGeoJson: {
      get() {
        return this.$store.state.RiverWarn.selectedRwGeoJson;
      },
    },
    /** 水防団モード */
    fldCtlParty: {
      get() {
        return this.$store.state.app.fldCtlParty;
      },
    },
  },
  methods: {
    clickRiverWarnList(e) {
      // 画面遷移パラメータ設定
      this.$store.commit("RiverWarn/setSelectedWarnReq", {
        cd: e.sourceTarget.feature.properties.cd,
        type: e.sourceTarget.feature.properties.type,
      });
      // 画面遷移
      if (this.$store.state.app.mode == "pc") {
        this.routerPushRw("/pc/rw");
      } else {
        this.routerPushRw("/mb/rwdetail");
      }
    },
    /**
     * 表示範囲の河川予警報GeoJsonを取得
     */
    getRiverWarnGeoJson() {
      // 地図番号が0(1枚表示か4枚表示の1枚目)のときのみ処理を行う
      if (this.mapNum == 0) {
        // 取得時間リスト
        let getTimeList = [];
        if (
          this.$store.state.radar.radarNumber === 4 ||
          this.$store.getters["radar/isReadyMap"]
        ) {
          // 4枚表示または動画再生
          // レーダの履歴動画再生時間リストを5分or10分間隔に切り捨てる
          getTimeList = MapUtil.getRoundTimeList(
            this.$store.state.radar.historyPlayList,
            this.$store.state.app.config.appConfig.keepDaysJsonFiles,
            this.$store.state.app.appTimeRoundRw
          );
        } else {
          // 1枚表示
          getTimeList = [this.baseTime];
        }
        // ズームレベルが規定値未満の場合は全国ファイルを参照
        let nationwideRef = false;
        if (this.$store.state.map.zoom < this.mapConfig.zoomReferencePrefs) {
          nationwideRef = true;
        }
        this.$store.dispatch("RiverWarn/getRiverWarnGeoJson", {
          towns: this.$store.state.map.towns,
          prefs: this.$store.state.map.prefs,
          prefTownType: this.$store.state.map.prefTownType,
          getTimeList: getTimeList,
          fldCtlParty: this.$store.getters["app/isFldCtl"],
          nationwide: nationwideRef,
        });
      }
    },
    /**
     * 選択中河川予警報GeoJsonを取得
     */
    getSelectedRwGeoJson() {
      if (this.selectedWarnReq && this.selectedWarnReq.type != 30) {
        this.$store.dispatch("RiverWarn/getSelectedRwGeoJson", {
          rwCd: this.selectedWarnReq.cd,
          rwType: this.selectedWarnReq.type,
        });
      }
    },
    // Pane設定
    setPane(geoJson) {
      if (geoJson.features[0].properties.type == 10) {
        // 洪水予報
        return "fldfrPane";
      } else if (geoJson.features[0].properties.type == 11) {
        // 水位周知河川情報
        return "evstgPane";
      } else if (geoJson.features[0].properties.type == 20) {
        // 水防警報
        return "fldctlPane";
      }
      return null;
    },
    // 予警報区間の線の太さと透過度の設定
    setRwLineConfig(type) {
      let rwLineConfig = {};

      // ズームレベルごとに設定ファイルの線の太さと透過度を設定する
      if (this.$store.state.map.zoom < type.middle.zoomLevel) {
        rwLineConfig.weight = type.shrink.weight;
        rwLineConfig.opacity = type.shrink.opacity;
      } else if (this.$store.state.map.zoom < type.enlarge.zoomLevel) {
        rwLineConfig.weight = type.middle.weight;
        rwLineConfig.opacity = type.middle.opacity;
      } else {
        rwLineConfig.weight = type.enlarge.weight;
        rwLineConfig.opacity = type.enlarge.opacity;
      }
      return rwLineConfig;
    },
  },
  /** 監視部 */
  watch: {
    /** 市町村リストが更新された時に河川予警報GeoJsonを取得する */
    getTownsAcqTime() {
      this.getRiverWarnGeoJson();
    },
    /** 観測日時を監視 */
    baseTime() {
      // 履歴動画未再生状態の場合のみ取得処理を行う
      if (this.$store.getters["radar/isNone"]) {
        this.getRiverWarnGeoJson();
      }
    },
    /** 地図の表示時間間隔が更新された時に河川予警報GeoJsonを取得する */
    radarInterval() {
      this.getRiverWarnGeoJson();
    },
    /** レーダ履歴動画再生状態 */
    radarHistoryPlayStatus() {
      // 「地図準備状態」の場合のみGeoJson取得を行う
      if (this.$store.getters["radar/isReadyMap"]) {
        this.getRiverWarnGeoJson();
      }
    },
    /** 選択中河川予警報リクエストキー */
    selectedWarnReq() {
      this.getSelectedRwGeoJson();
    },
    /** 水防団モードが更新された時に河川予警報GeoJsonを取得する */
    fldCtlParty() {
      this.getRiverWarnGeoJson();
    },
  },
};
</script>
